import React from 'react';
import { 
    Card, Button, Badge, Collapse 
} from 'react-bootstrap';
import { 
    FaFolder, FaFolderOpen, FaFileAlt, FaChevronDown, 
    FaChevronRight, FaEdit, FaTrash, FaPlus
} from 'react-icons/fa';

const FolderTreeView = ({ 
    folders, 
    forms, 
    departments, 
    expandedFolders, 
    onToggleExpansion, 
    onEditFolder, 
    onDeleteFolder, 
    viewMode = 'tree' 
}) => {
    const getDepartmentName = (departmentId) => {
        const dept = departments.find(d => d.department_id === departmentId);
        return dept ? dept.name : 'No Department';
    };

    const getFormCountForFolder = (folderId) => {
        return forms.filter(form => form.folder_id === folderId).length;
    };

    const getSubfolders = (parentId) => {
        return folders.filter(folder => folder.parent_folder_id === parentId);
    };

    const getRootFolders = () => {
        return folders.filter(folder => !folder.parent_folder_id);
    };

    const renderFolderItem = (folder, level = 0) => {
        const subfolders = getSubfolders(folder.folder_id);
        const formCount = getFormCountForFolder(folder.folder_id);
        const isExpanded = expandedFolders.has(folder.folder_id);
        const hasChildren = subfolders.length > 0;

        return (
            <div key={folder.folder_id} className="folder-tree-item">
                <div 
                    className="folder-item d-flex align-items-center p-2 border-bottom"
                    style={{ paddingLeft: `${20 + (level * 20)}px` }}
                >
                    {hasChildren ? (
                        <Button
                            variant="link"
                            size="sm"
                            className="p-0 me-2 text-secondary"
                            onClick={() => onToggleExpansion(folder.folder_id)}
                        >
                            {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
                        </Button>
                    ) : (
                        <span className="me-4"></span>
                    )}
                    
                    <div className="folder-icon me-2">
                        {isExpanded || !hasChildren ? 
                            <FaFolderOpen className="text-primary" /> : 
                            <FaFolder className="text-secondary" />
                        }
                    </div>
                    
                    <div className="folder-details flex-grow-1">
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <strong>{folder.name}</strong>
                                {folder.description && (
                                    <div className="small text-muted">
                                        {folder.description}
                                    </div>
                                )}
                                <div className="small text-info">
                                    {getDepartmentName(folder.department_id)}
                                </div>
                            </div>
                            
                            <div className="folder-stats d-flex align-items-center gap-2">
                                <Badge bg="primary">
                                    <FaFileAlt className="me-1" />
                                    {formCount}
                                </Badge>
                                
                                {hasChildren && (
                                    <Badge bg="info">
                                        <FaFolder className="me-1" />
                                        {subfolders.length}
                                    </Badge>
                                )}
                                
                                <div className="folder-actions">
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        className="me-1"
                                        onClick={() => onEditFolder(folder)}
                                    >
                                        <FaEdit />
                                    </Button>
                                    <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={() => onDeleteFolder(folder)}
                                    >
                                        <FaTrash />
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                {hasChildren && (
                    <Collapse in={isExpanded}>
                        <div>
                            {subfolders.map(subfolder => 
                                renderFolderItem(subfolder, level + 1)
                            )}
                        </div>
                    </Collapse>
                )}
            </div>
        );
    };

    const renderTreeView = () => {
        const rootFolders = getRootFolders();
        
        if (rootFolders.length === 0) {
            return (
                <div className="text-center p-4">
                    <FaFolder size={48} className="text-muted mb-3" />
                    <h6 className="text-muted">No folders found</h6>
                    <p className="text-muted">Create your first folder to organize forms.</p>
                </div>
            );
        }

        return (
            <div className="folder-tree">
                {rootFolders.map(folder => renderFolderItem(folder))}
            </div>
        );
    };

    const renderListView = () => {
        if (folders.length === 0) {
            return (
                <div className="text-center p-4">
                    <FaFolder size={48} className="text-muted mb-3" />
                    <h6 className="text-muted">No folders found</h6>
                    <p className="text-muted">Create your first folder to organize forms.</p>
                </div>
            );
        }

        return (
            <div className="folder-list">
                {folders.map(folder => (
                    <Card key={folder.folder_id} className="mb-2">
                        <Card.Body className="p-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <div className="d-flex align-items-center">
                                    <FaFolder className="text-primary me-3" size={24} />
                                    <div>
                                        <h6 className="mb-1">{folder.name}</h6>
                                        {folder.description && (
                                            <p className="text-muted small mb-1">{folder.description}</p>
                                        )}
                                        <small className="text-info">
                                            {getDepartmentName(folder.department_id)}
                                        </small>
                                    </div>
                                </div>
                                
                                <div className="d-flex align-items-center gap-2">
                                    <Badge bg="primary">
                                        <FaFileAlt className="me-1" />
                                        {getFormCountForFolder(folder.folder_id)} forms
                                    </Badge>
                                    
                                    <Badge bg="info">
                                        <FaFolder className="me-1" />
                                        {getSubfolders(folder.folder_id).length} subfolders
                                    </Badge>
                                    
                                    <div className="folder-actions">
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            className="me-1"
                                            onClick={() => onEditFolder(folder)}
                                        >
                                            <FaEdit />
                                        </Button>
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={() => onDeleteFolder(folder)}
                                        >
                                            <FaTrash />
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                ))}
            </div>
        );
    };

    const renderGridView = () => {
        if (folders.length === 0) {
            return (
                <div className="text-center p-4">
                    <FaFolder size={48} className="text-muted mb-3" />
                    <h6 className="text-muted">No folders found</h6>
                    <p className="text-muted">Create your first folder to organize forms.</p>
                </div>
            );
        }

        return (
            <div className="row">
                {folders.map(folder => (
                    <div key={folder.folder_id} className="col-md-6 col-lg-4 mb-3">
                        <Card className="h-100 folder-card">
                            <Card.Body className="text-center">
                                <FaFolder className="text-primary mb-3" size={48} />
                                <h6 className="card-title">{folder.name}</h6>
                                {folder.description && (
                                    <p className="card-text small text-muted">
                                        {folder.description}
                                    </p>
                                )}
                                <div className="mb-3">
                                    <Badge bg="primary" className="me-2">
                                        <FaFileAlt className="me-1" />
                                        {getFormCountForFolder(folder.folder_id)}
                                    </Badge>
                                    <Badge bg="info">
                                        <FaFolder className="me-1" />
                                        {getSubfolders(folder.folder_id).length}
                                    </Badge>
                                </div>
                                <small className="text-info d-block mb-3">
                                    {getDepartmentName(folder.department_id)}
                                </small>
                                <div className="d-flex justify-content-center gap-1">
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={() => onEditFolder(folder)}
                                    >
                                        <FaEdit />
                                    </Button>
                                    <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={() => onDeleteFolder(folder)}
                                    >
                                        <FaTrash />
                                    </Button>
                                </div>
                            </Card.Body>
                        </Card>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div className="folder-tree-view">
            {viewMode === 'tree' && renderTreeView()}
            {viewMode === 'list' && renderListView()}
            {viewMode === 'grid' && renderGridView()}
        </div>
    );
};

export default FolderTreeView;